import {UI} from "../../stem-core/src/ui/UIBase";
import {iFrameConnection} from "../services/IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {iFrameState} from "../services/IFrameState";
import {GATE_TYPE, PANEL_TYPE, POPUP_GATE_TRANSITION_TIME, WALL_TRANSITION_TIME} from "../../blink-sdk/Constants";
import {queueMicrotaskWrapper} from "../AsyncAggregateDispatcher";
import {isSmallScreen} from "../UtilsLib";
import {registerStyle} from "../../stem-core/src/ui/style/Theme";
import {styleRule} from "../../stem-core/src/decorators/Style";
import {StyleSheet} from "../../stem-core/src/ui/Style";
import {Dispatcher} from "../../stem-core/src/base/Dispatcher";


export class PaywallPageStyle extends StyleSheet {
    @styleRule
    paywallPage = {
        fontFamily: this.themeProps.MERCHANT_FONT_FAMILY,
        color: this.themeProps.MERCHANT_TEXT_PRIMARY_COLOR,
        top: 0,
        margin: "auto",
        ">:first-child": {
            marginTop: iFrameState.panelType === PANEL_TYPE.custom ? "" : "0 !important",
        },
        ">:last-child": {
            marginBottom: iFrameState.panelType === PANEL_TYPE.custom ? "" : "0 !important",
        }
    };

    @styleRule
    paywallPagePopup = {
        padding: isSmallScreen() ? "36px 18px !important" : "36px 30px !important",
    };

    @styleRule
    visibilityTransition = {
        transition: WALL_TRANSITION_TIME + "ms opacity ease",
    };

    @styleRule
    visible = {
        opacity: 1,
    };

    @styleRule
    hidden = {
        opacity: 0,
    };
}

let lastDispatchedHeight = null;
let lastDispatchedWidth = null;

@registerStyle(PaywallPageStyle)
export class PaywallPage extends UI.Element {
    visible = false;

    getMaxHeight() {
        const screenHeight = iFrameState.screen.height;
        return isSmallScreen() ? screenHeight - 40 : screenHeight;
    }

    getMaxWidth() {
        const screenWidth = iFrameState.screen.width;
        return isSmallScreen() ? screenWidth - 20 : screenWidth;
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        // TODO: We want to check if this class is in the widget iframe so that we maintain the added padding during the collapse transition.
        if (iFrameState.gateType === GATE_TYPE.popup) {
            attr.addClass(this.styleSheet.paywallPagePopup);
        }
        attr.setStyle("maxWidth", this.getMaxWidth());
        attr.setStyle("maxHeight", this.getMaxHeight());
        attr.addClass(this.styleSheet.paywallPage);
        attr.addClass(this.styleSheet.visibilityTransition);
        if (this.visible) {
            attr.addClass(this.styleSheet.visible);
        } else {
            attr.addClass(this.styleSheet.hidden);
        }
        if (this.widthValue) {
            attr.setStyle("width", "100%");
        }
        if (this.heightValue) {
            attr.setStyle("height", "100%");
        }
        if (!iFrameState.panelContent || iFrameState.gateType !== GATE_TYPE.banner) {
            attr.setStyle({
                position: "absolute",
                transform: "translateX(-50%)",
                left: "50%",
            });
        }
    }

    dispatchSizeChange() {
        const dispatchHeight = () => {
            if (this.isInDocument()) {
                // Currently we dispatch a size change only if it is actually a change. Otherwise, it might create some
                // code loops.
                const height = Math.min(this.heightValue || this.getHeight(), this.getMaxHeight());
                const width = Math.min(this.widthValue || this.getWidth(), this.getMaxWidth());

                if (height === lastDispatchedHeight && width === lastDispatchedWidth) {
                    clearTimeout(this.timeout);
                    cancelAnimationFrame(this.animationFrame);
                    return;
                }

                if (height) {
                    clearTimeout(this.timeout);
                    cancelAnimationFrame(this.animationFrame);
                }
                lastDispatchedHeight = height;
                lastDispatchedWidth = width;
                iFrameConnection.sendToSDK(IFrameMessages.UPDATE_PANEL_SIZE, {height, width});
            }
        };

        clearTimeout(this.timeout);
        cancelAnimationFrame(this.animationFrame);

        queueMicrotaskWrapper(dispatchHeight);
        this.timeout = this.attachTimeout(dispatchHeight);
        this.animationFrame = requestAnimationFrame(dispatchHeight);
    }

    show() {
        this.visible = true;
        this.addClass(this.styleSheet.visible);
    }

    hide() {
        this.visible = false;
        this.addClass(this.styleSheet.hidden);
    }

    onMount() {
        super.onMount();
        this.attachTimeout(() => this.show());
        this.attachUpdateListener(iFrameState, () => this.redraw());
        this.addListener("urlEnter", () => {
            this.redraw();
            this.dispatchSizeChange();
            this.attachTimeout(() => this.dispatchSizeChange(), POPUP_GATE_TRANSITION_TIME + 50);
            this.show();
        });
        this.addListener("urlExit", () => {
            this.hide();
            lastDispatchedHeight = null;
            lastDispatchedWidth = null;
        });
        this.attachListener(iFrameConnection, IFrameMessages.RECOMPUTE_PANEL_SIZE, () => this.dispatchSizeChange());

        const observer = new MutationObserver(() => this.dispatchSizeChange());
        const config = {childList: true, subtree: true, characterData: true};
        observer.observe(this.node, config);

        this.attachListener(Dispatcher.Global, "iframeSizeChange", () => this.dispatchSizeChange());
    }
}
